import styled, { css } from 'styled-components';
import { exFont } from '../../shared/typography';

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 888px;
  margin: 0 auto;
  padding: 28px 28px 48px;
  box-sizing: border-box;
`;

export const Heading = styled.h2`
  ${exFont(700, 'xxl')}
  line-height: normal;
  margin: 0;
  color: var(--ex-ink);
`;

// Drop zones (tplA 825, 846): 1.5px dashed #c9bff0, radius 14, hover
// #fbf8fe; a file dragged over turns the border purple on #f7f1ff.
const dropBase = css`
  display: flex;
  align-items: center;
  border: 1.5px dashed
    ${({ $over }) => ($over ? 'var(--ex-purple)' : 'var(--ex-drop-line)')};
  border-radius: 14px;
  background: ${({ $over }) =>
    $over ? 'var(--ex-plan-tint)' : 'var(--ex-surface)'};
  cursor: pointer;
  box-sizing: border-box;
  &:hover {
    background: ${({ $over }) =>
      $over ? 'var(--ex-plan-tint)' : 'var(--ex-selected-tint)'};
  }
`;

export const DropFirst = styled.div`
  ${dropBase}
  --ex-drop-line: #c9bff0;
  flex-direction: column;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  & > button {
    ${exFont(600, 16)}
    min-height: 48px;
    padding: 0 32px;
    margin-bottom: 12px;
    border: none;
    border-radius: 999px;
    line-height: normal;
  }
`;

export const DropFirstText = styled.p`
  ${exFont(400, 14.5)}
  margin: 0 0 6px;
  line-height: normal;
  color: var(--ex-ink-strong);
`;

export const DropFirstKinds = styled.p`
  ${exFont(400, 12.5)}
  margin: 0;
  line-height: normal;
  color: var(--ex-ink-2);
`;

export const DropMore = styled.div`
  ${dropBase}
  --ex-drop-line: #c9bff0;
  gap: 16px;
  padding: 22px 24px;
  & > div:last-child {
    flex: 1;
  }
`;

export const DropMoreTitle = styled.p`
  ${exFont(600, 15)}
  margin: 0;
  line-height: normal;
  color: var(--ex-ink);
`;

export const DropMoreDetail = styled.p`
  ${exFont(400, 13)}
  margin: 3px 0 0;
  line-height: normal;
  color: var(--ex-ink-2);
`;

/** "browse": a purple word inside the Drop more line (tplA 847). */
export const InlineLink = styled.button`
  font: inherit;
  border: none;
  background: none;
  padding: 0;
  color: var(--ex-purple);
  cursor: pointer;
`;

export const DropIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ex-purple);
  ${({ $more }) =>
    $more &&
    css`
      width: 46px;
      height: 46px;
      flex: 0 0 auto;
      border-radius: 12px;
      background: var(--ex-purple-100);
    `}
  ${({ $large }) =>
    $large &&
    css`
      width: 56px;
      height: 56px;
      margin-bottom: 16px;
      border-radius: 14px;
      background: var(--ex-purple-100);
    `}
`;

export const Text = styled.p`
  ${exFont(500, 's')}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const Strong = styled.p`
  ${exFont(600, 'm')}
  margin: 0;
  color: var(--ex-ink);
`;

export const Muted = styled.p`
  ${exFont(400, 'xs')}
  margin: 0;
  color: var(--ex-ink-muted);
`;

// Files card (tplA 832) and typed responses card (tplA 852).
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--ex-line);
  border-radius: 14px;
  background: var(--ex-surface);
  overflow: hidden;
  ${({ $upload }) =>
    $upload &&
    css`
      padding: 18px 22px;
      gap: 14px;
    `}
  ${({ $typed }) =>
    $typed &&
    css`
      padding: 14px 22px;
      gap: 10px;
    `}
`;

export const CardHead = styled.div`
  ${exFont(600, 15)}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  line-height: normal;
  color: var(--ex-ink);
`;

/** "All uploaded" / "Uploading 2 of 3" (tplA 833: 500 13px #56515f). */
export const CardStatus = styled.span`
  ${exFont(500, 13)}
  line-height: normal;
  color: var(--ex-ink-2);
  white-space: nowrap;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--ex-line);
  &:last-child {
    border-bottom: none;
  }
  ${({ $file }) =>
    $file &&
    css`
      padding: 0;
      border: 0;
      gap: 14px;
      & > svg {
        color: var(--ex-bad);
        width: 22px;
        height: 22px;
      }
    `}
`;

export const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
`;

export const RowLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

export const RowName = styled.span`
  ${exFont(500, 's')}
  font-size: 14px;
  line-height: normal;
  color: var(--ex-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled.span`
  ${exFont(400, 12.5)}
  line-height: normal;
  color: ${({ $bad }) => ($bad ? 'var(--ex-bad-ink)' : 'var(--ex-ink-2)')};
  white-space: nowrap;
`;

export const Bar = styled.div`
  height: 5px;
  border-radius: 999px;
  background: #eee8fa;
  overflow: hidden;
  & > span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: ${({ $complete }) =>
      $complete ? 'var(--ex-ok)' : 'var(--ex-purple)'};
    transition: width 0.2s ease;
  }
`;

// ✕ Remove (tplA 840): a 16px line ✕ in #56515f at 70% opacity.
export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: var(--ex-ink-2);
  opacity: 0.7;
  cursor: pointer;
  &:hover {
    opacity: 1;
  }
`;

export const TypedBadge = styled.span`
  ${exFont(700, 12)}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  color: var(--ex-purple);
  background: var(--ex-purple-100);
`;

export const Question = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 16px;
  flex-wrap: wrap;
  border-radius: 12px;
  /* Unanswered at Continue: the design's amber nudge (tplA 845, annotBd). */
  border: 1.5px solid
    ${({ $highlight }) => ($highlight ? 'var(--ex-warn)' : 'var(--ex-line)')};
  background: var(--ex-surface);
  & > div:first-child {
    flex: 1;
    min-width: 240px;
  }
  & > div:first-child > p:first-child {
    font-size: 14px;
    line-height: normal;
    color: var(--ex-purple);
  }
  & > div:first-child > p:nth-child(2) {
    font-size: 13px;
    line-height: 1.45;
    margin-top: 2px;
    color: var(--ex-ink-strong);
  }
`;

export const Required = styled.span`
  color: var(--ex-bad);
`;

export const Choice = styled.div`
  display: flex;
  gap: 8px;
`;

export const ChoiceButton = styled.button`
  ${exFont(600, 's')}
  min-height: 36px;
  padding: 0 18px;
  font-size: 14px;
  border-radius: 999px;
  cursor: pointer;
  border: 1.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ex-ink)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
`;

export const LinkButton = styled.button`
  ${exFont(600, 's')}
  border: none;
  background: none;
  padding: 0;
  color: var(--ex-purple);
  cursor: pointer;
  text-align: left;
`;

/** "+ Paste a typed response instead" (tplA 853: 600 14px purple). */
export const PasteLink = styled(LinkButton)`
  font-size: 14px;
  line-height: normal;
`;

// "Paste a typed response" (tplA 854–859).
export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px;
  border: 1.5px solid #d9c6fb;
  border-radius: 14px;
  background: var(--ex-surface);
`;

export const FormHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const FormTitle = styled.p`
  ${exFont(600, 15)}
  flex: 1;
  margin: 0;
  line-height: normal;
  color: var(--ex-ink);
`;

export const FormCancel = styled(LinkButton)`
  font-size: 13.5px;
  line-height: normal;
  color: var(--ex-ink-2);
`;

const field = css`
  ${exFont(400, 14.5)}
  width: 100%;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--ex-bad)' : 'var(--ex-line-strong)')};
  background-color: var(--ex-surface);
  color: var(--ex-ink);
  &:focus {
    outline: none;
    border-color: var(--ex-purple);
  }
`;

export const Input = styled.input`
  ${field}
  height: 42px;
`;

export const Select = styled.select.attrs({ className: 'inset-select-arrow' })`
  ${field}
  height: 42px;
  padding: 0 34px 0 12px;
  font-weight: 500;
`;

export const TextArea = styled.textarea`
  ${field}
  min-height: 160px;
  padding: 12px;
  line-height: 1.6;
  resize: vertical;
`;

export const FormFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const FormCount = styled.span`
  ${exFont(400, 12.5)}
  flex: 1;
  line-height: normal;
  color: var(--ex-ink-2);
`;

export const FormAnother = styled(LinkButton)`
  font-size: 13.5px;
  line-height: normal;
`;

export const FormAdd = styled.button`
  ${exFont(600, 14)}
  height: 38px;
  padding: 0 18px;
  border: none;
  border-radius: 999px;
  background: var(--ex-purple);
  color: var(--white);
  cursor: pointer;
  &:disabled {
    background: var(--ex-disabled-bg);
    cursor: default;
  }
`;

export const Banner = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: var(--ex-radius-item);
  color: ${({ $tone }) =>
    $tone === 'info' ? 'var(--ex-purple)' : 'var(--ex-bad-ink)'};
  background: ${({ $tone }) =>
    $tone === 'info' ? 'var(--ex-purple-100)' : 'var(--ex-bad-bg)'};
`;

export const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  max-width: 560px;
  margin: 48px auto;
  padding: 32px 28px;
  text-align: center;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
`;

export const Icon = styled.img`
  width: 48px;
  height: 48px;
`;

export const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--ex-surface-2);
  overflow: hidden;
  & > span {
    display: block;
    height: 100%;
    background: var(--ex-purple);
  }
`;

export const Hidden = styled.input`
  display: none;
`;

export const TypedAction = styled.div`
  text-align: center;
`;

/** One typed response (tplA 852): T · who · Typed · {n} words · ✕, gap 12. */
export const TypedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  & > span:nth-child(2) {
    flex: 1;
    min-width: 0;
  }
`;
